import {
  AUTOMATION_BUILDER_EXAMPLE_PROMPT_BODY_MAX,
  AUTOMATION_BUILDER_EXAMPLE_PROMPT_TITLE_MAX,
  automationBuilderExamplePromptSchema,
} from '@reach/shared-types/platforms';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { EXAMPLE_PROMPTS_COPY, PROMPT_WARNING_LENGTH, TITLE_WARNING_LENGTH } from './constants';
import styles from './ExamplePromptRow.module.css';
import type { ExamplePromptDraft } from './types';

interface ExamplePromptRowProps {
  draft: ExamplePromptDraft;
  position: number;
  isFirst: boolean;
  isLast: boolean;
  showErrors: boolean;
  onChange: (changes: Partial<ExamplePromptDraft>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

const invalidFieldsOf = (draft: ExamplePromptDraft) =>
  automationBuilderExamplePromptSchema.safeParse(draft).error?.issues.map(issue => issue.path[0]) ?? [];

const errorMessageFor = ({
  titleInvalid,
  promptInvalid,
}: {
  titleInvalid: boolean;
  promptInvalid: boolean;
}) => {
  if (titleInvalid && promptInvalid) return EXAMPLE_PROMPTS_COPY.missingTitleAndPrompt;
  if (titleInvalid) return EXAMPLE_PROMPTS_COPY.missingTitle;
  if (promptInvalid) return EXAMPLE_PROMPTS_COPY.missingPrompt;
  return undefined;
};

export const ExamplePromptRow = ({
  draft,
  position,
  isFirst,
  isLast,
  showErrors,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: ExamplePromptRowProps) => {
  const titleId = `example-prompt-${draft.id}-title`;
  const promptId = `example-prompt-${draft.id}-prompt`;
  const errorId = `example-prompt-${draft.id}-error`;

  const invalidFields = showErrors ? invalidFieldsOf(draft) : [];
  const titleInvalid = invalidFields.includes('title');
  const promptInvalid = invalidFields.includes('prompt');
  const errorMessage = errorMessageFor({ titleInvalid, promptInvalid });

  const titleCounter = `${draft.title.length}/${AUTOMATION_BUILDER_EXAMPLE_PROMPT_TITLE_MAX}`;
  const promptCounter = `${draft.prompt.length}/${AUTOMATION_BUILDER_EXAMPLE_PROMPT_BODY_MAX}`;
  const titleNearLimit = draft.title.length >= TITLE_WARNING_LENGTH;
  const promptNearLimit = draft.prompt.length >= PROMPT_WARNING_LENGTH;

  return (
    <li className={styles.examplePrompt}>
      <div className={styles.order}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={EXAMPLE_PROMPTS_COPY.moveUp(position)}
          disabled={isFirst}
          onClick={onMoveUp}>
          <ChevronUp />
        </Button>
        <span>{position}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={EXAMPLE_PROMPTS_COPY.moveDown(position)}
          disabled={isLast}
          onClick={onMoveDown}>
          <ChevronDown />
        </Button>
      </div>

      <fieldset className={styles.fields}>
        <legend>{EXAMPLE_PROMPTS_COPY.promptGroupLabel(position)}</legend>

        <label htmlFor={titleId}>{EXAMPLE_PROMPTS_COPY.titleLabel}</label>
        <span data-near-limit={titleNearLimit}>{titleCounter}</span>
        <Input
          id={titleId}
          value={draft.title}
          maxLength={AUTOMATION_BUILDER_EXAMPLE_PROMPT_TITLE_MAX}
          placeholder={EXAMPLE_PROMPTS_COPY.titlePlaceholder}
          aria-invalid={titleInvalid}
          aria-describedby={titleInvalid ? errorId : undefined}
          onChange={event => onChange({ title: event.target.value })}
        />
        <p>{EXAMPLE_PROMPTS_COPY.titleHelp}</p>

        <label htmlFor={promptId}>{EXAMPLE_PROMPTS_COPY.promptLabel}</label>
        <span data-near-limit={promptNearLimit}>{promptCounter}</span>
        <textarea
          id={promptId}
          rows={3}
          value={draft.prompt}
          maxLength={AUTOMATION_BUILDER_EXAMPLE_PROMPT_BODY_MAX}
          placeholder={EXAMPLE_PROMPTS_COPY.promptPlaceholder}
          aria-invalid={promptInvalid}
          aria-describedby={promptInvalid ? errorId : undefined}
          onChange={event => onChange({ prompt: event.target.value })}
        />

        {errorMessage && (
          <p id={errorId} role="alert" className={styles.error}>
            {errorMessage}
          </p>
        )}
      </fieldset>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className={styles.remove}
        aria-label={EXAMPLE_PROMPTS_COPY.removeLabel(position)}
        onClick={onRemove}>
        {EXAMPLE_PROMPTS_COPY.remove}
      </Button>
    </li>
  );
};
